import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import { Trash2, AlertTriangle, CheckCircle, Loader2, ArrowLeft } from 'lucide-react';

const AccountDeletion = () => {
  const { user, isAuthenticated, signout } = useAuth();
  const [email, setEmail] = useState('');
  const [reason, setReason] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    const requestEmail = isAuthenticated ? user?.email : email;
    if (!requestEmail) {
      setError('Please enter your email address.');
      return;
    }
    if (!confirmed) {
      setError('Please confirm that you understand this action is irreversible.');
      return;
    }

    setLoading(true);
    try {
      await api.post('/auth/request-account-deletion', {
        email: requestEmail,
        reason: reason || 'No reason provided'
      });
      setSubmitted(true);
      if (isAuthenticated) {
        await signout();
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to submit request. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center px-4">
        <div className="max-w-md w-full text-center">
          <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-6" />
          <h1 className="text-2xl font-bold mb-4">Request Submitted</h1>
          <p className="text-gray-400 mb-6">
            Your account deletion request has been received. We will process it within 30 days
            and send a confirmation to your email address.
          </p>
          <a href="/" className="inline-block px-6 py-3 bg-cyan-400 text-black rounded-lg font-medium hover:bg-cyan-300 transition-colors">
            Back to Home
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white">
      <div className="max-w-2xl mx-auto px-4 py-12">
        <a href="/" className="inline-flex items-center space-x-2 text-gray-400 hover:text-white mb-8 transition-colors">
          <ArrowLeft className="h-4 w-4" />
          <span>Back to Home</span>
        </a>

        <div className="flex items-center space-x-3 mb-6">
          <Trash2 className="h-8 w-8 text-red-500" />
          <h1 className="text-3xl font-bold">Delete Account</h1>
        </div>

        <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 mb-8">
          <div className="flex items-start space-x-3">
            <AlertTriangle className="h-5 w-5 text-red-500 mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-red-400 font-medium mb-1">This action is permanent</p>
              <p className="text-gray-400 text-sm">
                Requesting account deletion will permanently remove your account and all associated data, including your profile, watch history, favorites, comments, ratings, and any active subscriptions. This cannot be undone.
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white/5 border border-white/10 rounded-xl p-6 mb-8">
          <h2 className="text-lg font-semibold mb-4">What will be deleted:</h2>
          <ul className="space-y-2 text-gray-400 text-sm">
            <li className="flex items-center space-x-2"><span className="text-red-400">-</span><span>Your account profile and personal information</span></li>
            <li className="flex items-center space-x-2"><span className="text-red-400">-</span><span>Watch history and continue watching data</span></li>
            <li className="flex items-center space-x-2"><span className="text-red-400">-</span><span>Favorites and saved content</span></li>
            <li className="flex items-center space-x-2"><span className="text-red-400">-</span><span>Comments and ratings</span></li>
            <li className="flex items-center space-x-2"><span className="text-red-400">-</span><span>Purchase history and subscription data</span></li>
            <li className="flex items-center space-x-2"><span className="text-red-400">-</span><span>Video reviews and submissions</span></li>
          </ul>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {!isAuthenticated && (
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Email Address
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter the email associated with your account"
                className="w-full px-4 py-3 bg-white/5 border border-white/20 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-cyan-400 transition-colors"
                required
                data-testid="deletion-email-input"
              />
            </div>
          )}

          {isAuthenticated && (
            <div className="bg-white/5 border border-white/10 rounded-lg p-4">
              <p className="text-sm text-gray-400">Signed in as:</p>
              <p className="text-white font-medium">{user?.email}</p>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Reason for leaving (optional)
            </label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Help us improve by sharing why you're leaving..."
              rows={3}
              className="w-full px-4 py-3 bg-white/5 border border-white/20 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-cyan-400 transition-colors resize-none"
              data-testid="deletion-reason-input"
            />
          </div>

          <label className="flex items-start space-x-3 cursor-pointer">
            <input
              type="checkbox"
              checked={confirmed}
              onChange={(e) => setConfirmed(e.target.checked)}
              className="mt-1 h-4 w-4 rounded accent-red-500"
              data-testid="deletion-confirm-checkbox"
            />
            <span className="text-sm text-gray-400">
              I understand that this action is <span className="text-red-400 font-medium">irreversible</span> and all my data will be permanently deleted within 30 days.
            </span>
          </label>

          {error && (
            <p className="text-red-400 text-sm" data-testid="deletion-error">{error}</p>
          )}

          <button
            type="submit"
            disabled={loading || !confirmed}
            className="w-full flex items-center justify-center space-x-2 py-3 rounded-lg bg-red-600 text-white font-semibold hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="deletion-submit-button"
          >
            {loading ? (
              <Loader2 className="h-5 w-5 animate-spin" />
            ) : (
              <>
                <Trash2 className="h-5 w-5" />
                <span>Request Account Deletion</span>
              </>
            )}
          </button>
        </form>

        <p className="text-xs text-gray-500 mt-8 text-center">
          If you have any questions, contact us at contact@pulsemedia.vip
        </p>
      </div>
    </div>
  );
};

export default AccountDeletion;
